import React, { useState, useEffect } from "react";
import {
  MapPin,
  Clock,
  DollarSign,
  ExternalLink,
  Search,
  ChevronLeft,
  ChevronRight,
  IndianRupee,
} from "lucide-react";
import { Link } from "react-router-dom";
import { query, collection, orderBy, limit, getDocs } from "firebase/firestore";
import { db } from "../../config/firebase";

const JobsSection = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredJobs, setFilteredJobs] = useState([]);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [jobsPerPage] = useState(4); // Show fewer jobs per page in home section

  useEffect(() => {
    const fetchRecentJobs = async () => {
      try {
        const jobsQuery = query(
          collection(db, "jobs"),
          orderBy("postedAt", "desc"),
          limit(10) // limit to 10 documents
        );
        const querySnapshot = await getDocs(jobsQuery);
        const jobsList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          postedDate: doc.data().postedAt,
        }));
        setJobs(jobsList);
        setFilteredJobs(jobsList);
        setLoading(false);
      } catch (err) {
        console.error("[JobsSection] Error fetching jobs:", err);
        setError("Failed to load jobs. Please try again later.");
        setLoading(false);
      }
    };

    fetchRecentJobs();
  }, []);

  useEffect(() => {
    // Filter jobs whenever searchTerm changes
    const filtered = jobs.filter(
      (job) =>
        job.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.skills?.some((skill) =>
          skill.toLowerCase().includes(searchTerm.toLowerCase())
        )
    );
    setFilteredJobs(filtered);
    setCurrentPage(1); // Reset to first page when search changes
  }, [searchTerm, jobs]);

  // Get current jobs for pagination
  const indexOfLastJob = currentPage * jobsPerPage;
  const indexOfFirstJob = indexOfLastJob - jobsPerPage;
  const currentJobs = filteredJobs.slice(indexOfFirstJob, indexOfLastJob);
  const totalPages = Math.ceil(filteredJobs.length / jobsPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) {
    return (
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-end mb-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Latest Job Opportunities
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl">
                Loading recent job listings...
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {[...Array(4)].map((_, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 animate-pulse"
              >
                <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2 mb-3"></div>
                <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
                <div className="flex space-x-2">
                  <div className="h-8 bg-gray-200 rounded w-24"></div>
                  <div className="h-8 bg-gray-200 rounded w-24"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-red-600">
              Error Loading Jobs
            </h2>
            <p className="text-lg text-gray-600 mb-8">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium"
            >
              Try Again
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="relative py-16 bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900">
      <div className="absolute inset-0 bg-gradient-to-t from-blue-900/50 to-transparent"></div>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12">
          <div className="mb-6 md:mb-0">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white drop-shadow-lg">
              Latest Job Opportunities
            </h2>
            <p className="text-lg text-blue-100 max-w-2xl">
              {searchTerm
                ? `Found ${filteredJobs.length} jobs matching "${searchTerm}"`
                : `Showing ${filteredJobs.length} recent jobs. Find your next career opportunity.`}
            </p>
          </div>

          {/* Search Bar */}
          <div className="w-full md:w-auto">
            <div className="relative">
              <input
                type="text"
                placeholder="Search jobs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full md:w-64 pl-10 pr-4 py-3 bg-white/10 backdrop-blur-md border-2 border-white/20 rounded-xl focus:border-white/40 focus:ring-2 focus:ring-white/10 transition-all text-white placeholder-white/70"
              />
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/70" />
            </div>
          </div>
        </div>

        {currentJobs.length === 0 ? (
          <div className="text-center py-12 bg-white/5 backdrop-blur-md rounded-2xl border border-white/10">
            <p className="text-lg text-blue-100">
              No jobs found matching your search.
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {currentJobs.map((job) => (
                <div
                  key={job.id}
                  className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 hover:border-white/40 transition-all duration-300"
                >
                  <JobCard key={job.id} job={job} />
                </div>
              ))}
            </div>

            {/* Pagination */}
            {filteredJobs.length > jobsPerPage && (
              <div className="flex justify-center items-center space-x-2 mt-8">
                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`p-2 rounded-xl ${
                    currentPage === 1
                      ? "text-blue-300/50 cursor-not-allowed"
                      : "text-white hover:bg-white/10"
                  }`}
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>

                {[...Array(totalPages)].map((_, index) => (
                  <button
                    key={index + 1}
                    onClick={() => paginate(index + 1)}
                    className={`px-4 py-2 rounded-xl ${
                      currentPage === index + 1
                        ? "bg-white text-blue-700 font-medium"
                        : "text-white hover:bg-white/10"
                    }`}
                  >
                    {index + 1}
                  </button>
                ))}

                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`p-2 rounded-xl ${
                    currentPage === totalPages
                      ? "text-blue-300/50 cursor-not-allowed"
                      : "text-white hover:bg-white/10"
                  }`}
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            )}
          </>
        )}

        <div className="text-center mt-10">
          <Link
            to="/jobs"
            className="inline-flex items-center text-white font-medium hover:text-blue-200 transition-colors bg-white/10 hover:bg-white/20 px-6 py-3 rounded-xl backdrop-blur-md"
          >
            View all jobs
            <ExternalLink className="inline ml-2 h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

const JobCard = ({ job }) => {
  const formatDate = (timestamp) => {
    if (!timestamp) return "Recently posted";

    const date = timestamp instanceof Date ? timestamp : timestamp.toDate();
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  return (
    <div className="p-6 h-full">
      <div className="flex-grow">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-white mb-2 md:mb-0">
            {job.title}
          </h3>
          <span className="bg-white/20 text-white text-xs font-medium px-3 py-1 rounded-full md:ml-2 inline-block w-fit">
            {job.type || "Full-time"}
          </span>
        </div>

        <p className="text-blue-100 font-medium mb-4">{job.company}</p>

        <div className="flex flex-col sm:flex-row sm:items-center text-sm text-blue-100 mb-4 gap-3">
          <span className="flex items-center">
            <MapPin className="h-4 w-4 mr-1 text-blue-200" />
            {job.location || "Remote"}
          </span>
          {job.salary && (
            <span className="flex items-center">
              <IndianRupee className="h-4 w-4 mr-1 text-blue-200" />
              {job.salary}
            </span>
          )}
          <span className="flex items-center">
            <Clock className="h-4 w-4 mr-1 text-blue-200" />
            {formatDate(job.createdAt)}
          </span>
        </div>

        {job.skills && (
          <div className="flex flex-wrap gap-2 mb-4">
            {job.skills.map((skill, index) => (
              <span
                key={index}
                className="bg-white/10 text-blue-100 text-xs px-3 py-1 rounded-full"
              >
                {skill}
              </span>
            ))}
          </div>
        )}

        {job.description && (
          <p className="text-blue-100 text-sm mb-4 line-clamp-2 opacity-80">
            {job.description}
          </p>
        )}

        <div className="flex flex-col sm:flex-row gap-2 mt-4">
          <Link
            to={`/jobs/${job.id}`}
            className="bg-white text-blue-700 hover:bg-blue-50 px-4 py-2 rounded-xl text-sm font-medium transition-colors text-center"
          >
            View Details
          </Link>
          <button className="bg-white/10 hover:bg-white/20 text-white border border-white/20 hover:border-white/40 px-4 py-2 rounded-xl text-sm font-medium transition-colors">
            Save Job
          </button>
        </div>
      </div>
    </div>
  );
};

export default JobsSection;
